import { Check, Repeat, Video } from "lucide-react";
import type { ReactNode } from "react";

import { serviceColorStyle } from "@/components/booking-schedule/lib/colors";
import { Section, SectionHead, panel } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/** A surface on the dark slab: the shared panel, with a hairline so its edge stays crisp. */
const tile = cn(panel, "ring-1 ring-border/50");

const FIRST_HOUR = 7;
const LAST_HOUR = 15;
const HOURS = Array.from({ length: LAST_HOUR - FIRST_HOUR + 1 }, (_, index) => FIRST_HOUR + index);
/** Pixels per hour on the timeline. */
const HOUR = 60;
/** Air between two things that sit back to back. */
const GAP = 2;
const NOW = 9 * 60 + 40;

const at = (hour: number, minute = 0) => hour * 60 + minute;
const clock = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
const span = (from: number, to: number) => `${clock(from)} – ${clock(to)}`;
/** Where a minute of the day lands on the timeline. */
const offset = (minutes: number) => ((minutes - FIRST_HOUR * 60) / 60) * HOUR;
const slotStyle = (from: number, to: number) => ({
  top: offset(from) + 1,
  height: offset(to) - offset(from) - GAP,
});

/**
 * A sample day. Tidy times on purpose: it is the shape of a day, not anybody's. It adds up to what
 * the facts beside it say: five visits, two of them waiting for a yes.
 */
const visits = (): {
  id: string;
  title: string;
  initials?: string;
  color: string;
  from: number;
  to: number;
  confirmed?: boolean;
  pending?: boolean;
}[] => [
  {
    id: "ola",
    title: m.platform_preview_ola(),
    initials: "OK",
    color: "blue",
    from: at(7),
    to: at(7, 50),
  },
  {
    id: "kuba",
    title: m.platform_preview_kuba(),
    initials: "KM",
    color: "lime",
    from: at(8),
    to: at(9),
    confirmed: true,
  },
  {
    id: "consult",
    title: m.platform_preview_consult(),
    color: "cyan",
    from: at(10, 15),
    to: at(11),
    pending: true,
  },
  {
    id: "ania",
    title: m.platform_preview_ania(),
    initials: "AW",
    color: "purple",
    from: at(12, 15),
    to: at(13, 15),
    confirmed: true,
  },
  {
    id: "marta",
    title: m.platform_preview_marta(),
    initials: "MN",
    color: "orange",
    from: at(13, 30),
    to: at(14, 30),
    pending: true,
  },
];

const Visit = ({ visit }: { visit: ReturnType<typeof visits>[number] }) => (
  <div
    style={{
      ...serviceColorStyle(visit.color),
      ...slotStyle(visit.from, visit.to),
    }}
    className={cn(
      "absolute inset-x-2 flex items-center gap-2.5 overflow-hidden rounded-[10px] border px-2.5",
      // Same language as the real board: a request that still needs a yes is hollow and dashed.
      visit.pending
        ? "border-dashed border-[color-mix(in_oklab,var(--service)_70%,transparent)] bg-[color-mix(in_oklab,var(--service)_5%,var(--card))]"
        : "border-[color-mix(in_oklab,var(--service)_28%,transparent)] bg-[color-mix(in_oklab,var(--service)_16%,var(--card))]",
    )}
  >
    {/* On a phone the words need the room more than the face does. */}
    <span className="grid size-7 shrink-0 place-items-center rounded-full bg-[color-mix(in_oklab,var(--service)_30%,var(--card))] text-[10px] font-semibold tracking-wide max-sm:hidden">
      {visit.initials ?? <Video className="size-3.5" />}
    </span>
    <div className="min-w-0 flex-1">
      {/* One line each, so a short visit never cuts a word in half; "confirmed" is a mark. */}
      <p className="flex items-center gap-1.5 text-[13px] leading-4 font-medium">
        <span className="truncate">{visit.title}</span>
        {visit.confirmed ? (
          <span
            title={m.platform_preview_confirmed()}
            className="grid size-3.5 shrink-0 place-items-center rounded-full bg-(--service) text-background"
          >
            <Check className="size-2.5" strokeWidth={3.5} />
          </span>
        ) : null}
      </p>
      <p className="truncate text-xs leading-4 text-muted-foreground tabular-nums">{span(visit.from, visit.to)}</p>
    </div>
    {visit.pending ? (
      <span className="shrink-0 rounded-full bg-[color-mix(in_oklab,var(--service)_18%,var(--card))] px-2 py-0.5 text-[11px] font-medium whitespace-nowrap max-sm:hidden">
        {m.platform_preview_pending()}
      </span>
    ) : null}
  </div>
);

const DayCard = () => {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat(getLocale(), {
    weekday: "long",
  }).format(now);
  const date = new Intl.DateTimeFormat(getLocale(), {
    day: "numeric",
    month: "short",
  }).format(now);

  return (
    <div className={cn(tile, "flex min-w-0 flex-col p-5")}>
      <div className="flex items-center justify-between gap-3 pb-5">
        <b className="truncate text-[15px] font-semibold first-letter:uppercase">
          {weekday} <span className="font-normal text-muted-foreground">· {date}</span>
        </b>
        <div className="flex shrink-0 rounded-full bg-[var(--site-well)] p-[3px] text-xs font-medium text-muted-foreground max-sm:hidden">
          {[m.platform_view_day(), m.platform_view_week(), m.platform_view_month()].map((view, index) => (
            <span
              key={view}
              className={cn(
                "rounded-full px-3 py-1",
                index === 0 && "bg-[color-mix(in_oklch,var(--foreground)_12%,var(--card))] text-foreground",
              )}
            >
              {view}
            </span>
          ))}
        </div>
      </div>
      <div className="relative mt-1.5 mb-1.5 grid grid-cols-[48px_1fr]" style={{ height: offset(LAST_HOUR * 60) }}>
        {/* Each hour's label sits on its own line, the way the real board has it. */}
        <div className="relative text-[11px] text-muted-foreground tabular-nums">
          {HOURS.map((hour) => (
            <span key={hour} className="absolute right-3 -translate-y-1/2" style={{ top: offset(at(hour)) }}>
              {clock(at(hour))}
            </span>
          ))}
          <span className="absolute right-3 -translate-y-1/2 font-semibold text-primary" style={{ top: offset(NOW) }}>
            {clock(NOW)}
          </span>
        </div>
        <div className="relative">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute inset-x-0 border-t border-border/50"
              style={{ top: offset(at(hour)) }}
            />
          ))}
          <div
            style={slotStyle(at(11, 30), at(12))}
            className="absolute inset-x-2 flex items-center gap-2 overflow-hidden rounded-[10px] bg-[repeating-linear-gradient(135deg,var(--site-well)_0_6px,transparent_6px_12px)] px-3 text-xs text-muted-foreground"
          >
            <span className="font-medium">{m.platform_preview_break()}</span>
            <span className="truncate tabular-nums">{span(at(11, 30), at(12))}</span>
          </div>
          {visits().map((visit) => (
            <Visit key={visit.id} visit={visit} />
          ))}
          <div className="absolute inset-x-0 z-10 border-t border-primary" style={{ top: offset(NOW) }}>
            <span className="absolute top-0 left-0 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
          </div>
        </div>
      </div>
    </div>
  );
};

/** The next four Tuesdays, for the series tile. */
const nextTuesdays = () => {
  const today = new Date();
  const ahead = (2 - today.getDay() + 7) % 7 || 7;
  return [0, 1, 2, 3].map(
    (week) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + ahead + week * 7),
  );
};

const Fact = ({
  value,
  label,
  lit,
  children,
}: {
  value: string;
  label: string;
  lit?: boolean;
  children: ReactNode;
}) => (
  <div className={cn(tile, "flex flex-col p-5", lit && "bg-primary text-primary-foreground ring-0")}>
    <b className="text-[44px] leading-none font-medium tracking-[-0.05em] tabular-nums">{value}</b>
    <span className={cn("mt-2 text-sm", lit ? "opacity-75" : "text-muted-foreground")}>{label}</span>
    <div className="mt-auto pt-4">{children}</div>
  </div>
);

/** One bar per visit in the day: the settled ones solid, the two still waiting faint. */
const DayBars = () => (
  <div className="flex gap-1">
    {visits().map((visit) => (
      <i
        key={visit.id}
        className={cn("h-1.5 flex-1 rounded-full bg-primary-foreground", visit.pending && "opacity-25")}
      />
    ))}
  </div>
);

const WaitingList = () => (
  <ul className="grid gap-2 text-[13px]">
    {visits()
      .filter((visit) => visit.pending)
      .map((visit) => (
        <li key={visit.id} style={serviceColorStyle(visit.color)} className="flex items-center gap-2">
          <i className="size-2 shrink-0 rounded-full border-[1.5px] border-(--service)" />
          <span className="min-w-0 flex-1 truncate">{visit.title}</span>
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{clock(visit.from)}</span>
        </li>
      ))}
  </ul>
);

const SeriesDates = () => {
  const format = new Intl.DateTimeFormat(getLocale(), { day: "numeric", month: "short" });
  return (
    <div className="flex flex-wrap gap-1.5">
      {nextTuesdays().map((date) => (
        <span
          key={date.toISOString()}
          className="rounded-full bg-[var(--site-well)] px-2.5 py-1 text-xs font-medium whitespace-nowrap tabular-nums"
        >
          {format.format(date)}
        </span>
      ))}
    </div>
  );
};

/**
 * The product shot: a trainer's day on the timeline, and beside it what the day adds up to. A
 * drawing of the schedule, not the schedule: nothing here is anybody's real calendar.
 */
export const PlatformPreview = () => (
  <Section id="preview">
    <SectionHead
      eyebrow={m.platform_preview_kicker()}
      title={m.platform_preview_title()}
      aside={m.platform_preview_lede()}
    />
    <div
      aria-hidden
      className="relative isolate grid gap-3 overflow-hidden rounded-[32px] border border-border/50 bg-[color-mix(in_oklab,var(--primary)_3%,var(--solid-background))] p-4 before:absolute before:inset-x-[10%] before:-bottom-3/5 before:-z-10 before:h-full before:bg-[radial-gradient(closest-side,var(--site-glow),transparent)] md:p-8 lg:grid-cols-[1.6fr_1fr]"
    >
      <DayCard />
      <div className="grid gap-3 sm:grid-cols-3 lg:auto-rows-fr lg:grid-cols-1">
        <Fact value="5" label={m.platform_day_visits()} lit>
          <DayBars />
        </Fact>
        <Fact value="2" label={m.platform_day_pending()}>
          <WaitingList />
        </Fact>
        <div className={cn(tile, "flex flex-col p-5")}>
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold">
            <Repeat className="size-4 text-[var(--site-ink)]" />
            {m.platform_day_series_title()}
          </span>
          <span className="mt-2 text-sm text-muted-foreground">{m.platform_day_series()}</span>
          <div className="mt-auto pt-4">
            <SeriesDates />
          </div>
        </div>
      </div>
    </div>
  </Section>
);
